if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.cardQuantity = this.querySelector('[data-card-quantity]');
      if (this.cardQuantity) {
        this.cardQuantityInput = this.cardQuantity.querySelector('[data-card-quantity-input]');
        this.cardQuantityInput.addEventListener('change', () => this.normalizeCardQuantity());
        this.cardQuantity.querySelectorAll('[data-card-quantity-change]').forEach((button) => {
          button.addEventListener('click', () => {
            if (this.cardQuantityPending) return;
            const quantity = this.normalizeCardQuantity();
            this.cardQuantityInput.value = quantity + Number(button.dataset.cardQuantityChange) * Number(this.cardQuantityInput.step || 1);
            this.normalizeCardQuantity();
          });
        });
        this.normalizeCardQuantity();
      }
      this.cardVariantSelect = this.querySelector('[data-card-variant-select]');
      if (this.cardVariantSelect) {
        this.cardVariantSelect.addEventListener('change', () => this.selectCardVariant());
        this.selectCardVariant();
      }
    }

    selectCardVariant() {
      const option = this.cardVariantSelect.selectedOptions[0];
      const selected = Boolean(option && option.value && !option.disabled);
      this.form.querySelector('[name=id]').value = selected ? option.value : '';
      this.querySelector('[data-card-pack-actions]').hidden = !selected;
      this.submitButton.disabled = !selected;
      this.handleErrorMessage();
      if (!selected) return;
      this.cardQuantityInput.min = option.dataset.min || '1';
      this.cardQuantityInput.step = option.dataset.step || '1';
      this.cardQuantityInput.max = option.dataset.max || '';
      this.cardQuantityInput.value = this.cardQuantityInput.min;
      this.normalizeCardQuantity();
      const price = this.closest('.card').querySelector('.card-information .price');
      const template = this.querySelector('template[data-variant-price="' + option.value + '"]');
      if (price && template) price.replaceWith(template.content.cloneNode(true));
    }

    normalizeCardQuantity() {
      if (!this.cardQuantity) return;
      const value = Number(this.cardQuantityInput.value);
      const min = Math.max(1, Number(this.cardQuantityInput.min) || 1);
      const step = Math.max(1, Number(this.cardQuantityInput.step) || 1);
      const max = Number(this.cardQuantityInput.max) || Number.MAX_SAFE_INTEGER;
      const bounded = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;
      const quantity = min + Math.floor((bounded - min) / step) * step;
      this.cardQuantityInput.value = quantity;
      this.cardQuantity.querySelector('[data-card-quantity-change="-1"]').disabled = this.cardQuantityPending || quantity <= min;
      this.cardQuantity.querySelector('[data-card-quantity-change="1"]').disabled = this.cardQuantityPending || quantity + step > max;
      return quantity;
    }

    setCardQuantityPending(pending) {
      if (!this.cardQuantity) return;
      this.cardQuantityPending = pending;
      if (this.cardVariantSelect) this.cardVariantSelect.disabled = pending;
      this.cardQuantityInput.disabled = pending;
      this.cardQuantity.setAttribute('aria-busy', String(pending));
      this.normalizeCardQuantity();
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsedDocument = new DOMParser().parseFromString(html || '', 'text/html');
      const selectedElement = parsedDocument.querySelector(selector);

      return selectedElement ? selectedElement.innerHTML : parsedDocument.body.innerHTML;
    }

    updateCartIconFromResponse(response) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      const sectionHtml =
        response &&
        response.sections &&
        response.sections['cart-icon-bubble'];

      if (!cartIcon || !sectionHtml) return false;

      const sectionInnerHtml = this.getSectionInnerHTML(sectionHtml);
      if (!sectionInnerHtml) return false;

      cartIcon.innerHTML = sectionInnerHtml;
      return true;
    }

    async refreshCartIcon() {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!cartIcon) return;

      const rootUrl =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      try {
        const response = await fetch(`${rootUrl}?sections=cart-icon-bubble`, {
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) return;

        const sections = await response.json();
        this.updateCartIconFromResponse({ sections: sections });
      } catch (error) {
        console.error('Unable to refresh cart icon.', error);
      }
    }

    renderCartContents(response) {
      if (!this.cart) return;

      try {
        this.cart.renderContents(response);
      } catch (error) {
        console.error('Unable to render the cart notification.', error);
        this.refreshCartIcon();
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.normalizeCardQuantity();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      this.setCardQuantityPending(true);

      const add = async () => {
        const response = await fetch(`${routes.cart_add_url}`, config).then(result => result.json());
        if (response.status || !window.TRDPackConversion) return response;
        try {
          const converted = await window.TRDPackConversion.normalize(null, this.cart?.getSectionsToRender().map(section => section.id));
          return converted ? {...converted.item, sections: converted.cart.sections} : response;
        } catch (error) {
          window.TRDPackConversion.review();
          throw error;
        }
      };
      (window.TRDPackConversion ? window.TRDPackConversion.run(add) : add())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);

            if (this.cardQuantity) {
              this.error = false;
              return;
            }

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          if (this.cardQuantity) {
            this.cardQuantityInput.value = this.cardQuantityInput.min || '1';
            this.normalizeCardQuantity();
          }

          const iconUpdated = this.updateCartIconFromResponse(response);
          if (!iconUpdated) this.refreshCartIcon();

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id')
            });
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => {
                this.renderCartContents(response);
              });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.renderCartContents(response);
          }
        })
        .catch((error) => {
          console.error(error);
          if (this.cardQuantity && window.cartStrings) this.handleErrorMessage(window.cartStrings.error);
        })
        .finally(() => {
          this.setCardQuantityPending(false);
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
